<template>
  <UDropdownMenu
    v-if="organizations.length > 1"
    :items="items"
    :content="{ align: 'start', collisionPadding: 12 }"
    :ui="{ content: collapsed ? 'w-64' : 'w-(--reka-dropdown-menu-trigger-width) min-w-64' }"
  >
    <UTooltip :text="organizationName ?? ''" :disabled="!collapsed">
      <UButton
        color="neutral"
        variant="ghost"
        block
        :square="collapsed"
        :loading="isSwitching"
        :aria-label="$t('organizationSwitcher.label')"
        class="data-[state=open]:bg-elevated"
      >
        <UIcon name="i-lucide-building-2" class="size-5 shrink-0" />
        <span v-if="!collapsed" class="min-w-0 flex-1 truncate text-left text-sm font-medium text-highlighted">
          {{ organizationName }}
        </span>
        <UIcon v-if="!collapsed" name="i-lucide-chevrons-up-down" class="size-4 shrink-0 text-dimmed" />
      </UButton>
    </UTooltip>
  </UDropdownMenu>
</template>

<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import { useUserStore } from '~~/stores/useUserStore'

defineProps<{
  collapsed?: boolean
}>()

const { t: $t } = useI18n()
const toast = useToast()
const { organization, organizations } = storeToRefs(useUserStore())

const organizationName = computed(() => organization.value?.name ?? null)
const isSwitching = ref(false)

const items = computed<DropdownMenuItem[][]>(() => [
  [{ type: 'label', label: $t('organizationSwitcher.label') }],
  organizations.value.map((candidate) => {
    const isActive = candidate.id === organization.value?.id
    return {
      label: candidate.name,
      icon: isActive ? 'i-lucide-check' : 'i-lucide-building-2',
      active: isActive,
      onSelect: () => switchOrganization(candidate.id, isActive)
    }
  })
])

async function switchOrganization(organizationId: string, isActive: boolean) {
  if (isActive || isSwitching.value) return

  isSwitching.value = true
  try {
    await $fetch('/auth/organization', { method: 'POST', body: { organizationId } })
    // A full reload drops every store and list that belongs to the previous organization.
    // The home page is the target because the open form belongs to the previous organization.
    // `force` is required: Nuxt skips a reload of the same path within 10 seconds,
    // so a quick switch back would leave the page unchanged with a spinning button.
    reloadNuxtApp({ path: '/', persistState: false, force: true })
  } catch {
    isSwitching.value = false
    toast.add({ title: $t('organizationSwitcher.error'), color: 'error' })
  }
}
</script>
